import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Heart, ArrowLeft, Shield } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SupporterCard = ({ s, i, anon, locale }) => (
  <div
    data-testid={`supporter-card-${i}`}
    className="fade-up group rounded-xl border border-white/10 bg-black/45 p-5 flex items-center gap-4 hover:border-[#f5b400]/60 hover:-translate-y-0.5 transition-[transform,border-color] duration-300"
    style={{ animationDelay: `${Math.min(i, 12) * 60}ms` }}
  >
    <div className="w-11 h-11 rounded-full bg-[#f5b400]/15 border border-[#f5b400]/40 flex items-center justify-center shrink-0">
      {s.name ? <Heart size={18} className="text-[#f5b400]" fill="currentColor" /> : <Shield size={18} className="text-white/50" />}
    </div>
    <div className="min-w-0">
      <p className={`font-heading text-lg truncate ${s.name ? 'text-white' : 'text-white/55'}`}>{s.name || anon}</p>
      <p className="font-body text-[11px] text-white/45">
        {s.date ? new Date(s.date).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' }) : ''}
      </p>
    </div>
  </div>
);

const SupportersPage = () => {
  const { t, lang } = useLang();
  const [list, setList] = useState(null);

  useEffect(() => {
    axios.get(`${API}/donations/wall`).then(({ data }) => setList(data.supporters)).catch(() => setList([]));
  }, []);

  return (
    <div className="pt-28 pb-20 bg-[#0a0608] min-h-screen" data-testid="supporters-page">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <Link to="/donate" data-testid="supporters-back-link" className="inline-flex items-center gap-2 text-[#f5b400] font-nav text-xs hover:text-white transition-colors">
          <ArrowLeft size={14} /> {t('wall.back')}
        </Link>
        <div className="mt-6 text-center fade-up">
          <p className="font-nav text-[#f5b400] text-xs tracking-widest">{t('wall.eyebrow')}</p>
          <h1 className="font-heading section-heading-white mt-3">{t('wall.title_top')}</h1>
          <h1 className="font-heading section-heading-gold">{t('wall.title_bottom')}</h1>
          <p className="mt-4 max-w-xl mx-auto text-white/75 font-body text-sm">{t('wall.intro')}</p>
        </div>

        {list === null ? (
          <div className="mt-14 flex justify-center"><div className="w-10 h-10 border-2 border-[#f5b400] border-t-transparent rounded-full animate-spin" /></div>
        ) : list.length === 0 ? (
          <p data-testid="supporters-empty" className="mt-14 text-center text-white/60 font-body text-sm">{t('wall.empty')}</p>
        ) : (
          <div className="mt-12 flex flex-wrap justify-center gap-4" data-testid="supporters-grid">
            {list.map((s, i) => (
              <div key={i} className="w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.667rem)]">
                <SupporterCard s={s} i={i} anon={t('wall.anonymous')} locale={lang} />
              </div>
            ))}
          </div>
        )}

        <div className="mt-14 text-center">
          <Link to="/donate" data-testid="supporters-donate-cta" className="inline-flex items-center gap-2 bg-[#f5b400] hover:bg-[#ffc630] text-black font-nav text-xs px-8 py-4 rounded-md transition-colors">
            <Heart size={16} fill="currentColor" /> {t('wall.cta')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SupportersPage;
